import React, { useState, useEffect } from 'react';
import { CreditCard, Filter, Star, TrendingDown, Calculator, TrendingUp, RefreshCw, ArrowRight, Landmark } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import DashboardLayout from '../components/DashboardLayout';
import { generateDynamicLoans } from '../utils/dynamicLoans';
import { financialDataAPI } from '../services/apiServices';
import { toast } from '../hooks/use-toast';
import { useRef } from 'react';

// === 3D TILT CARD COMPONENT ===
const TiltCard = ({ children, className, depth = 5, glowColor = "rgba(34, 197, 94, 0.08)" }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -depth;
    const rotateY = ((x - centerX) / centerX) * depth;

    card.style.setProperty('--mouse-x', `${x}px`);
    card.style.setProperty('--mouse-y', `${y}px`);
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg)`;
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`relative transition-all duration-300 ease-out preserve-3d group ${className}`}
      style={{
        background: `radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), ${glowColor}, transparent 40%)`
      }}
    >
      {children}
    </div>
  );
};

const Loans = () => {
  const [filterType, setFilterType] = useState('all');
  const [interestRates, setInterestRates] = useState([]);
  const [mortgageRates, setMortgageRates] = useState(null); // Store latest mortgage rates
  const [loanCalculator, setLoanCalculator] = useState({ amount: 1000000, tenure: 20, rate: 8.5 });
  const [loans, setLoans] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [animatedEmi, setAnimatedEmi] = useState(0);

  // Eligibility State
  const [eligibilityData, setEligibilityData] = useState({ income: '', obligations: '' });
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 50/30/20 Rule & FOIR Implementation
  const checkEligibility = (currentEMI) => {
    const income = parseFloat(eligibilityData.income);
    const obligations = parseFloat(eligibilityData.obligations) || 0;

    if (!income || income <= 0) {
      toast({ title: "Input Required", description: "Please enter a valid monthly income.", variant: "destructive" });
      return;
    }

    // Professional FOIR (Fixed Obligation to Income Ratio) Logic
    // Standard Banks usually cap Total EMIs at 50% of Net Income
    const maxFOIR = 0.50;
    const maxTotalEMI = income * maxFOIR;
    const availableForNewEMI = maxTotalEMI - obligations;

    // 50/30/20 Rule Analysis (Contextual advice)
    // Needs (50%), Wants (30%), Savings/Debt (20%)

    const isEligible = currentEMI <= availableForNewEMI;

    setEligibilityResult({
      isEligible,
      maxEMI: availableForNewEMI > 0 ? availableForNewEMI : 0,
      disposable: income - obligations,
      foirMessage: isEligible
        ? "Great! This EMI fits well within standard bank eligibility norms (50% FOIR)."
        : `This EMI exceeds strict bank eligibility. Banks typically allow only up to ₹${Math.floor(availableForNewEMI).toLocaleString()} more in EMIs given your current obligations.`
    });
  };


  useEffect(() => {
    const fetchData = async () => {
      // 1. Fetch Dynamic Loans based on current RBI rate
      const dynamicLoans = await generateDynamicLoans();
      setLoans(dynamicLoans);
      setFilteredLoans(dynamicLoans);

      // 2. Fetch Benchmark Rate for loan calculator
      const benchmarkData = await financialDataAPI.getBenchmarkRate();
      if (benchmarkData) {
        setLoanCalculator(prev => ({ ...prev, rate: (benchmarkData.benchmark + 2.5).toFixed(2) }));
        // Also update interest rates state for display
        setInterestRates([{ name: 'Central Bank Rate', rate_pct: benchmarkData.benchmark }]);
      }

      // 3. Fetch Mortgage Rates (for additional display)
      const mRates = await financialDataAPI.getMortgageRates();
      if (mRates && mRates.length > 0) {
        setMortgageRates(mRates[0]);
      }
    };
    fetchData();
  }, []);

  // EMI Odometer Animation
  useEffect(() => {
    const P = parseFloat(loanCalculator.amount) || 0;
    const annualRate = parseFloat(loanCalculator.rate) || 0;
    const years = parseFloat(loanCalculator.tenure) || 0;
    const r = annualRate / 12 / 100;
    const n = years * 12;

    let emi = 0;
    if (n > 0) {
      if (annualRate === 0) {
        emi = P / n;
      } else {
        const commonParenthesis = Math.pow(1 + r, n);
        emi = (P * r * commonParenthesis) / (commonParenthesis - 1);
      }
    }

    // Protection against NaN/Infinity
    if (isNaN(emi) || !isFinite(emi)) emi = 0;

    const startValue = isNaN(animatedEmi) ? 0 : animatedEmi;
    const endValue = emi;
    let startTime = null;
    const duration = 800;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 4);
      const currentVal = startValue + (endValue - startValue) * ease;
      setAnimatedEmi(isNaN(currentVal) ? 0 : currentVal);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [loanCalculator]);

  // Determine loans to display: Dynamic Loans based on RBI rate
  const displayLoans = React.useMemo(() => {
    let loansList = [...loans];

    // If we have real mortgage rates, synthesize loan objects
    if (mortgageRates) {
      const realLoans = [];

      if (mortgageRates['30-year_fixed_rate']) {
        realLoans.push({
          id: 'real-30y',
          bankName: 'Market Mortgage (US Avg)',
          loanType: 'Home Loan (30Y Fixed)',
          interestRate: mortgageRates['30-year_fixed_rate'],
          rating: 5,
          details: 'Based on current market averages'
        });
      }
      if (mortgageRates['15-year_fixed_rate']) {
        realLoans.push({
          id: 'real-15y',
          bankName: 'Market Mortgage (US Avg)',
          loanType: 'Home Loan (15Y Fixed)',
          interestRate: mortgageRates['15-year_fixed_rate'],
          rating: 4.5,
          details: 'Based on current market averages'
        });
      }
      if (mortgageRates['5-year_arm']) {
        realLoans.push({
          id: 'real-5y',
          bankName: 'Market Mortgage (US Avg)',
          loanType: 'Home Loan (5Y ARM)',
          interestRate: mortgageRates['5-year_arm'],
          rating: 4.0,
          details: 'Adjustable Rate Mortgage'
        });
      }

      // Prioritize real loans if they exist
      if (realLoans.length > 0) {
        loansList = [...realLoans, ...loansList];
      }
    }

    // --- SMART RATE ADJUSTMENT ENGINE ---
    // Make the mock bank rates "live" by indexing them to the Real Repo Rate.
    const liveRepoRateObj = interestRates.find(r => r.name === 'Central Bank Rate' || r.name.includes('Repo'));

    if (liveRepoRateObj && liveRepoRateObj.rate_pct) {
      const liveRepo = parseFloat(liveRepoRateObj.rate_pct);
      const baselineRepo = 6.50; // Baseline used when creating mock data
      const delta = liveRepo - baselineRepo;

      if (delta !== 0) {
        loansList = loansList.map(loan => {
          // Don't adjust the "Real" API mortgage rates, only the Partner Bank mock data
          if (loan.id.includes('real')) return loan;

          return {
            ...loan,
            interestRate: parseFloat((loan.interestRate + delta).toFixed(2)),
            isDynamic: true // Flag to show UI tooltip
          };
        });
      }
    }

    if (filterType === 'all') return loansList;
    return loansList.filter(loan => loan.loanType.toLowerCase().includes(filterType.toLowerCase()));
  }, [filterType, interestRates, mortgageRates, loans]);

  const sortedLoans = [...displayLoans].sort((a, b) => {
    const getRate = (l) => typeof l.interestRate === 'number' ? l.interestRate : parseFloat(l.interestRate.split(' - ')[0]);
    return getRate(b) - getRate(a);
  });

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#020617] text-slate-100 p-4 md:p-8 font-sans selection:bg-[#16A34A]/30 overflow-x-hidden">
        <div className="mb-10 flex flex-col items-start animate-in fade-in slide-in-from-left-6 duration-1000">
          <div className="mb-3 flex items-center gap-2 bg-white/5 px-3 py-1 rounded-full border border-white/10">
            <Star size={14} className="text-[#22c55e]" />
            <span className="text-[8px] font-black uppercase tracking-[0.3em] text-[#22c55e]">Institutional Lending Desk</span>
          </div>
          <h1 className="text-xl md:text-3xl font-black tracking-tight text-[#F1F5F9] mb-2 uppercase">Loan Management</h1>
          <p className="text-[#94A3B8] text-sm max-w-xl font-medium leading-relaxed">
            Find the best loan rates and calculate SMART EMI with institutional-grade risk models.
          </p>
        </div>


        <div className="mb-6 p-4 rounded-2xl bg-slate-900/60 backdrop-blur-md border border-white/10 relative overflow-hidden shadow-2xl animate-in fade-in duration-700">
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#16A34A]/5 rounded-full blur-[80px]"></div>
          <div className="flex flex-col md:flex-row items-center space-x-0 md:space-x-4 gap-4">
            <div className="p-3 bg-[#16A34A]/10 rounded-xl text-[#22c55e] border border-[#16A34A]/20 shadow-2xl flex-shrink-0">
              <TrendingUp size={20} />
            </div>
            <div className="flex-1 text-center md:text-left w-full">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 gap-2">
                <div>
                  <h3 className="text-lg font-black text-[#F1F5F9] uppercase tracking-tight">Live Market Rates</h3>
                  <p className="text-[10px] font-bold text-[#16A34A]/70">Official Central Bank & Market Mortgage Rates</p>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {/* Central Bank Rates */}
                {interestRates.length > 0 ? (
                  interestRates.slice(0, 2).map((rate, idx) => (
                    <div key={idx} className="bg-black/40 px-3 py-2 rounded-lg border border-white/5 shadow-inner group hover:border-emerald-500/30 transition-all flex flex-col justify-center">
                      <p className="text-[8px] text-[#94A3B8] uppercase font-black tracking-widest mb-1 truncate">{rate.name}</p>
                      <div className="flex items-baseline gap-1">
                        <p className="text-lg font-black text-white">{rate.rate_pct}%</p>
                        <p className="text-[7px] text-emerald-500 font-bold uppercase">Policy</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-1 flex items-center gap-2 text-[#94A3B8] text-[9px] h-10 font-black uppercase">
                    <RefreshCw className="animate-spin" size={12} /> Fetching...
                  </div>
                )}

                {/* Mortgage Rates */}
                {mortgageRates ? (
                  <>
                    {mortgageRates['30-year_fixed_rate'] && (
                      <div className="bg-black/40 px-3 py-2 rounded-lg border border-white/5 shadow-inner group hover:border-[#16A34A]/30 transition-all flex flex-col justify-center">
                        <p className="text-[8px] text-[#94A3B8] uppercase font-black tracking-widest mb-1 truncate">30Y Fixed</p>
                        <div className="flex items-baseline gap-1">
                          <p className="text-lg font-black text-[#22c55e]">{mortgageRates['30-year_fixed_rate']}%</p>
                          <p className="text-[7px] text-[#16A34A]/50 font-bold uppercase">Mkt</p>
                        </div>
                      </div>
                    )}
                    {mortgageRates['15-year_fixed_rate'] && (
                      <div className="bg-black/40 px-3 py-2 rounded-lg border border-white/5 shadow-inner group hover:border-emerald-500/30 transition-all flex flex-col justify-center">
                        <p className="text-[8px] text-[#94A3B8] uppercase font-black tracking-widest mb-1 truncate">15Y Fixed</p>
                        <div className="flex items-baseline gap-1">
                          <p className="text-lg font-black text-emerald-400">{mortgageRates['15-year_fixed_rate']}%</p>
                          <p className="text-[7px] text-emerald-500/50 font-bold uppercase">Mkt</p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="col-span-1 flex items-center gap-2 text-[#94A3B8] text-[9px] font-bold h-10">
                    <RefreshCw className="animate-spin" size={12} /> Fetching...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="recommendations" className="space-y-10">
          <TabsList className="bg-white/5 backdrop-blur-2xl p-1.5 border border-white/10 rounded-xl flex w-fit">
            <TabsTrigger value="recommendations" className="data-[state=active]:bg-[#16A34A] data-[state=active]:text-white rounded-lg text-[#94A3B8] px-6 py-2 text-[11px] font-black uppercase tracking-widest transition-all">Loan Recommendations</TabsTrigger>
            <TabsTrigger value="smart-calculator" className="data-[state=active]:bg-[#16A34A] data-[state=active]:text-white rounded-lg text-[#94A3B8] px-6 py-2 text-[11px] font-black uppercase tracking-widest transition-all">Smart EMI Calculator</TabsTrigger>
          </TabsList>

          {/* Loan Recommendations Tab */}
          <TabsContent value="recommendations">


            {/* 2. Top Recommendation Banner */}
            {sortedLoans.length > 0 && (
              <div className="mb-14 overflow-hidden rounded-[2.5rem] border border-emerald-500/30 shadow-[0_0_50px_rgba(16,185,129,0.1)] relative group">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-400 via-lime-400 to-emerald-400 animate-pulse z-20"></div>
                <div className="absolute top-1.5 right-6 bg-[#16A34A] text-black text-[9px] font-black px-4 py-1.5 rounded-b-xl z-20 uppercase tracking-widest shadow-2xl">
                  Lowest Interest Rate Portfolio
                </div>
                <div className="grid md:grid-cols-3 bg-[#020617]">
                  <div className="p-10 md:col-span-2 relative">
                    <div className="absolute top-0 right-0 p-10 opacity-[0.03] pointer-events-none text-white"><Landmark size={120} /></div>
                    <div className="flex items-center gap-5 mb-8">
                      <div className="h-14 w-14 bg-[#16A34A]/10 border border-[#16A34A]/20 rounded-2xl flex items-center justify-center text-[#22c55e] font-black text-2xl shadow-2xl shadow-[#16A34A]/20">
                        %
                      </div>
                      <div>
                        <h3 className="text-3xl font-black text-[#F1F5F9] tracking-tighter uppercase">{sortedLoans[0].bankName}</h3>
                        <p className="text-[10px] font-black text-[#16A34A] uppercase tracking-[0.2em]">{sortedLoans[0].loanType}</p>
                      </div>
                    </div>
                    <p className="text-[#94A3B8] text-sm font-medium leading-relaxed max-w-xl">
                      This instrument offers a high-precision interest rate of <span className="font-black text-[#22c55e]">{sortedLoans[0].interestRate}%</span>.
                      {sortedLoans[0].id.includes('real') ? ' Verified via real-time market indices.' : ' Forecasted based on institutional partner data.'}
                    </p>
                    <div className="mt-8 flex flex-wrap gap-4">
                      <div className="bg-white/5 border border-white/10 px-4 py-2 rounded-xl text-[10px] font-medium text-[#94A3B8]">
                        Tier-1 approval metrics
                      </div>
                      <div className="bg-white/5 border border-white/10 px-4 py-2 rounded-xl text-[10px] font-medium text-[#94A3B8]">
                        Rapid digital induction
                      </div>
                    </div>
                  </div>
                  <div className="p-10 bg-[#16A34A]/[0.03] flex flex-col justify-center items-center text-center border-t md:border-t-0 md:border-l border-white/5">
                    <p className="text-[10px] font-black text-[#16A34A]/60 uppercase tracking-[0.3em] mb-3">Annual Interest Profile</p>
                    <p className="text-6xl font-black text-[#22c55e] mb-8 tracking-tighter">{sortedLoans[0].interestRate}<span className="text-2xl ml-1 opacity-60">%</span></p>
                    <Button
                      className="w-full h-14 bg-gradient-to-r from-[#16A34A] to-[#84CC16] hover:from-[#22c55e] hover:to-[#a3e635] text-white font-black uppercase text-[11px] tracking-[0.2em] rounded-2xl shadow-6xl shadow-green-900/40 transition-all active:scale-95 translate-y-0 hover:-translate-y-1"
                      onClick={() => {
                        if (sortedLoans[0].applyLink) {
                          window.open(sortedLoans[0].applyLink, '_blank');
                        } else {
                          toast({ title: "Redirecting...", description: "Opening application page..." });
                        }
                      }}
                    >
                      Apply Now
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10 border-b border-white/5 pb-8">
              <div className="max-w-2xl">
                <h2 className="text-2xl font-black text-[#F1F5F9] uppercase tracking-tight">Available Instruments</h2>
                <p className="text-[11px] font-bold text-[#94A3B8] mt-1 mb-4">Global lending registry</p>
                <div className="p-4 bg-amber-500/5 border border-amber-500/10 rounded-2xl">
                  <p className="text-[10px] text-amber-500/80 font-medium leading-relaxed">
                    <span className="font-black uppercase tracking-widest mr-2">Disclaimer:</span>
                    Interest rates shown are indicative and may change without notice. Final rates, eligibility, and loan terms depend on your credit profile and the lender’s policies. Please verify details directly with the respective bank or financial institution before applying.
                  </p>
                </div>
              </div>
              <div className="flex bg-white/5 p-1.5 rounded-2xl border border-white/10 group-focus-within:border-[#16A34A]/50 transition-all shadow-xl items-center w-full md:w-64">
                <Filter size={16} className="ml-3 text-[#94A3B8]" />
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="bg-transparent border-0 text-[10px] font-black uppercase tracking-widest focus:ring-0 text-[#F1F5F9]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-[#F1F5F9]">
                    <SelectItem value="all" className="text-[10px] font-black uppercase tracking-widest">All Loan Types</SelectItem>
                    <SelectItem value="home" className="text-[10px] font-black uppercase tracking-widest">Home Loan</SelectItem>
                    <SelectItem value="car" className="text-[10px] font-black uppercase tracking-widest">Car Loan</SelectItem>
                    <SelectItem value="personal" className="text-[10px] font-black uppercase tracking-widest">Personal Loan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sortedLoans.map((loan, index) => (
                <TiltCard key={loan.id} depth={12} className="bg-slate-800/70 border border-[#16A34A]/20 rounded-[2rem] p-8 flex flex-col hover:border-[#22c55e] transition-all duration-300 relative overflow-hidden group/card shadow-[0_0_20px_rgba(34,197,94,0.05)]">
                  {index === 0 && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-[#16A34A] to-[#84CC16] text-black text-[9px] font-black px-4 py-1.5 rounded-bl-2xl z-20 uppercase tracking-widest">
                      Institutional Pick
                    </div>
                  )}
                  <div className="flex justify-between items-start mb-8">
                    <div>
                      <h3 className="text-lg font-black text-[#F1F5F9] mb-2 uppercase tracking-tight">{loan.bankName}</h3>
                      <span className="inline-block px-3 py-1 bg-[#16A34A]/10 text-[#22c55e] text-[9px] font-black rounded-lg border border-[#16A34A]/20 uppercase tracking-widest">
                        {loan.loanType}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1.5 bg-black/30 px-2 py-1 rounded-lg">
                      <Star size={12} className="fill-yellow-400 text-yellow-400" />
                      <span className="text-[10px] font-black text-white">{loan.rating}</span>
                    </div>
                  </div>

                  <div className="bg-black/40 p-6 rounded-2xl border border-white/5 mb-8 shadow-inner group-hover/card:bg-black/60 transition-colors">
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-4xl font-black text-[#22c55e] tracking-tighter">{typeof loan.interestRate === 'number' ? loan.interestRate.toFixed(2) : loan.interestRate}</span>
                      <span className="text-xl font-black text-[#22c55e]/60 uppercase">%</span>
                    </div>
                    <p className="text-[9px] text-[#94A3B8] font-black uppercase tracking-[0.2em] flex items-center gap-2">
                      Interest Yield (P.A)
                      {loan.id.includes('real') && <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-md text-[7px] border border-blue-500/30">L-FIXED</span>}
                      {loan.isDynamic && <span className="bg-[#16A34A]/20 text-[#22c55e] px-2 py-0.5 rounded-md text-[7px] border border-[#16A34A]/30">SMART-INDEX</span>}
                    </p>
                  </div>

                  <div className="space-y-3 mb-8 flex-1">
                    <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-[#94A3B8]">
                      <span>Max Tenure</span>
                      <span className="text-slate-200">{loan.tenure || '30 Years'}</span>
                    </div>
                    <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-[#94A3B8]">
                      <span>Processing</span>
                      <span className="text-slate-200">{loan.processingFee || '0.50%'}</span>
                    </div>
                  </div>

                  <div className="mt-auto">
                    <Button
                      className="w-full bg-gradient-to-r from-[#16A34A] to-[#84CC16] hover:from-[#22c55e] hover:to-[#a3e635] text-white text-[11px] font-black uppercase rounded-2xl h-12 tracking-[0.15em] shadow-xl transition-all active:scale-95"
                      onClick={() => {
                        if (loan.applyLink) {
                          window.open(loan.applyLink, '_blank');
                        } else {
                          const query = encodeURIComponent(`${loan.bankName} ${loan.loanType} apply online`);
                          window.open(`https://www.google.com/search?q=${query}`, '_blank');
                        }
                      }}
                    >
                      Apply Now
                    </Button>
                  </div>
                </TiltCard>
              ))}
            </div>
          </TabsContent>

          {/* Smart Calculator Tab */}
          <TabsContent value="smart-calculator">
            {/* 1. Calculator Inputs */}
            <div className="grid lg:grid-cols-2 gap-10 items-stretch">
              {/* 1. Calculator Inputs */}
              <TiltCard depth={8} className="p-8 bg-[#0C0A09] rounded-[2.5rem] border border-white/5 shadow-3xl h-full flex flex-col relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-3xl rounded-full"></div>
                <div className="mb-10 flex items-center gap-4">
                  <div className="p-3 bg-[#16A34A]/10 rounded-2xl text-[#22c55e] border border-[#16A34A]/20 shadow-xl"><Calculator size={28} /></div>
                  <div>
                    <h2 className="text-xl font-black text-white tracking-tight uppercase">Smart EMI Calculator</h2>
                    <p className="text-[11px] font-bold text-[#16A34A]/70 mt-1">Institutional risk estimator</p>
                  </div>
                </div>

                <div className="space-y-10 flex-1">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center mb-2 px-1">
                      <Label className="text-[10px] font-black uppercase tracking-[0.3em] text-[#94A3B8]">Principal Capital (₹)</Label>
                      <span className="text-xs font-black text-[#22c55e] bg-[#16A34A]/5 px-2 py-1 rounded-lg border border-[#16A34A]/10 shadow-inner">₹{Number(loanCalculator.amount).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="relative group">
                      <Input
                        type="range"
                        min="100000" max="10000000" step="10000"
                        value={loanCalculator.amount}
                        onChange={(e) => setLoanCalculator({ ...loanCalculator, amount: e.target.value })}
                        className="loan-slider w-full h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer group-hover:bg-slate-700 transition-all border-none focus:ring-0"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center mb-2 px-1">
                      <Label className="text-[10px] font-black uppercase tracking-[0.3em] text-[#94A3B8]">Interest Index (% P.A)</Label>
                      <span className="text-xs font-black text-[#22c55e] bg-[#16A34A]/5 px-2 py-1 rounded-lg border border-[#16A34A]/10 shadow-inner">{loanCalculator.rate}%</span>
                    </div>
                    <div className="relative group">
                      <Input
                        type="range"
                        min="1" max="25" step="0.1"
                        value={loanCalculator.rate}
                        onChange={(e) => setLoanCalculator({ ...loanCalculator, rate: e.target.value })}
                        className="loan-slider w-full h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer group-hover:bg-slate-700 transition-all border-none focus:ring-0"
                      />
                    </div>
                    <p className="text-[10px] font-bold text-[#16A34A]/50 mt-2 ml-1">
                      *Benchmark Repo Rate indexed at {(interestRates.find(r => r.name.includes('Central'))?.rate_pct || '6.50')}%.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center mb-2 px-1">
                      <Label className="text-[10px] font-black uppercase tracking-[0.3em] text-[#94A3B8]">Liquidity Window (Years)</Label>
                      <span className="text-xs font-black text-[#22c55e] bg-[#16A34A]/5 px-2 py-1 rounded-lg border border-[#16A34A]/10 shadow-inner">{loanCalculator.tenure} YRS</span>
                    </div>
                    <div className="relative group">
                      <Input
                        type="range"
                        min="1" max="30" step="1"
                        value={loanCalculator.tenure}
                        onChange={(e) => setLoanCalculator({ ...loanCalculator, tenure: e.target.value })}
                        className="loan-slider w-full h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer group-hover:bg-slate-700 transition-all border-none focus:ring-0"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-6 mt-4 border-t border-white/5">
                    <div className="space-y-1">
                      <p className="text-[10px] font-bold text-[#94A3B8] mb-1">Absolute Input</p>
                      <Input
                        type="number"
                        value={loanCalculator.amount}
                        onChange={(e) => setLoanCalculator({ ...loanCalculator, amount: e.target.value })}
                        className="bg-slate-950 border-slate-800 text-[#F1F5F9] h-10 rounded-xl font-black text-xs px-3 shadow-inner"
                      />
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] font-bold text-[#94A3B8] mb-1">Duration Correction</p>
                      <Input
                        type="number"
                        value={loanCalculator.tenure}
                        onChange={(e) => setLoanCalculator({ ...loanCalculator, tenure: e.target.value })}
                        className="bg-slate-950 border-slate-800 text-[#F1F5F9] h-10 rounded-xl font-black text-xs px-3 shadow-inner"
                      />
                    </div>
                  </div>
                </div>
              </TiltCard>

              {/* 2. Results & Eligibility Card */}
              <div className="bg-[#020617] border border-white/5 rounded-[2.5rem] p-10 flex flex-col justify-between shadow-6xl relative overflow-hidden group/results animate-in fade-in duration-1000">
                {(() => {
                  const P = parseFloat(loanCalculator.amount) || 0;
                  const annualRate = parseFloat(loanCalculator.rate) || 0;
                  const years = parseFloat(loanCalculator.tenure) || 0;
                  const r = annualRate / 12 / 100;
                  const n = years * 12;

                  let emi = 0;
                  if (n > 0) {
                    if (annualRate === 0) {
                      emi = P / n;
                    } else {
                      const commonParenthesis = Math.pow(1 + r, n);
                      emi = (P * r * commonParenthesis) / (commonParenthesis - 1);
                    }
                  }

                  if (isNaN(emi) || !isFinite(emi)) emi = 0;

                  const totalPay = emi * n;
                  const totalInt = totalPay - P;

                  return (
                    <div className="space-y-12 relative z-10">
                      <div className="text-center md:text-left space-y-4">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                          <div className="flex-shrink-0">
                            <p className="text-[#16A34A]/70 text-[11px] font-bold mb-2">Monthly installment (Estimated)</p>
                            <div className="p-8 bg-[#16A34A]/5 rounded-[2rem] border border-[#16A34A]/10 backdrop-blur-3xl shadow-2xl relative overflow-hidden group-hover/results:border-[#16A34A]/30 transition-all min-w-[280px]">
                              <div className="absolute top-0 right-0 w-32 h-32 bg-[#16A34A]/10 blur-[60px] pointer-events-none"></div>
                              <h2 className={`font-black text-white tracking-tighter shadow-[#16A34A]/20 leading-none ${Math.round(animatedEmi).toString().length > 10 ? 'text-3xl' :
                                Math.round(animatedEmi).toString().length > 7 ? 'text-4xl' :
                                  'text-6xl'
                                }`}>
                                ₹{Math.round(animatedEmi).toLocaleString('en-IN')}
                              </h2>
                              <p className="text-[11px] font-bold text-white/40 mt-2">Digital projection</p>
                            </div>
                          </div>
                          <div className="space-y-4 md:text-right flex-1 min-w-0">
                            <h4 className="text-xl md:text-2xl font-black text-white uppercase tracking-tighter leading-tight shrink-0 whitespace-nowrap">Capital Convergence</h4>
                            <p className="text-[#94A3B8] text-[11px] font-medium leading-relaxed max-w-[240px] md:ml-auto mt-2">Cumulative debt settlement across the complete {loanCalculator.tenure}-year window.</p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6 pt-10 border-t border-white/5">
                        <div className="bg-black/30 p-6 rounded-2xl border border-white/5 shadow-inner hover:border-[#16A34A]/20 transition-all">
                          <p className="text-[#94A3B8] text-[10px] font-bold mb-2">Interest accrued</p>
                          <p className="text-2xl font-black text-rose-400 tracking-tight">₹{Math.round(totalInt).toLocaleString('en-IN')}</p>
                        </div>
                        <div className="bg-black/30 p-6 rounded-2xl border border-white/5 shadow-inner hover:border-[#16A34A]/20 transition-all">
                          <p className="text-[#94A3B8] text-[10px] font-bold mb-1">Aggregate repay</p>
                          <p className="text-2xl font-black text-[#22c55e] tracking-tight">₹{Math.round(totalPay).toLocaleString('en-IN')}</p>
                        </div>
                      </div>

                      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="w-full h-16 bg-white/5 hover:bg-[#16A34A] border border-white/10 hover:border-[#16A34A] text-white font-black uppercase text-[11px] tracking-[0.3em] rounded-2xl transition-all shadow-6xl group/btn">
                            <span className="flex items-center justify-center gap-3">
                              Execute Eligibility Scan <ArrowRight size={18} className="group-hover/btn:translate-x-1 transition-all" />
                            </span>
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[425px] bg-[#020617] border-white/10 text-white rounded-[2rem] p-8">
                          <DialogHeader className="mb-6">
                            <DialogTitle className="text-2xl font-black uppercase tracking-tight">Asset Eligibility Scan</DialogTitle>
                            <DialogDescription className="text-[#94A3B8] text-[11px] font-medium mt-2 leading-relaxed">
                              Real-time benchmarking for monthly installment of ₹{Math.round(emi).toLocaleString('en-IN')}.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-6 py-4">
                            <div className="space-y-2">
                              <Label className="text-[10px] font-black uppercase tracking-widest text-[#94A3B8]">Monthly Operational Income (₹)</Label>
                              <Input
                                type="number"
                                value={eligibilityData.income}
                                onChange={(e) => setEligibilityData({ ...eligibilityData, income: e.target.value })}
                                className="bg-slate-950 border-white/10 text-white h-12 rounded-xl font-black shadow-inner focus:ring-[#16A34A]"
                                placeholder="e.g. 100000"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-[10px] font-black uppercase tracking-widest text-[#94A3B8]">Current Debt Obligations (₹)</Label>
                              <Input
                                type="number"
                                value={eligibilityData.obligations}
                                onChange={(e) => setEligibilityData({ ...eligibilityData, obligations: e.target.value })}
                                className="bg-slate-950 border-white/10 text-white h-12 rounded-xl font-black shadow-inner focus:ring-[#16A34A]"
                                placeholder="e.g. 15000"
                              />
                            </div>
                          </div>

                          {eligibilityResult && (
                            <div className={`p-6 rounded-2xl mt-4 border border-white/10 animate-in zoom-in-95 duration-300 ${eligibilityResult.isEligible ? 'bg-[#16A34A]/5' : 'bg-rose-500/5'}`}>
                              <div className="flex items-center gap-3 mb-3">
                                <div className={`h-2 w-2 rounded-full animate-pulse ${eligibilityResult.isEligible ? 'bg-[#16A34A]' : 'bg-rose-500'}`} />
                                <h4 className={`text-sm font-black uppercase tracking-widest ${eligibilityResult.isEligible ? 'text-[#22c55e]' : 'text-rose-400'}`}>
                                  {eligibilityResult.isEligible ? 'Protocol Cleared' : 'Risk Alert'}
                                </h4>
                              </div>
                              <p className="text-[11px] font-bold text-slate-400 leading-relaxed">
                                {eligibilityResult.foirMessage}
                              </p>
                            </div>
                          )}

                          <DialogFooter className="mt-8">
                            <Button className="w-full h-14 bg-[#16A34A] hover:bg-[#16A34A] text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-xl" onClick={() => checkEligibility(emi)}>Initiate Scan</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  );
                })()}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <TiltCard depth={5} className="mt-16 p-8 rounded-[2rem] bg-white/2 border border-white/5 relative overflow-hidden group hover:border-white/10 transition-all">
          <div className="absolute top-0 left-0 w-1 h-full bg-[#16A34A]/50 group-hover:bg-[#22c55e] transition-colors"></div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-[#16A34A]/60 mb-3 flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#16A34A]"></span>
            </span>
            Registry Disclaimer
          </h4>
          <p className="text-[11px] text-[#94A3B8] font-bold leading-relaxed max-w-4xl">
            Interest rates shown are indicative and may change without notice. Final rates, eligibility, and loan terms depend on your credit profile and the lender’s policies. Please verify details directly with the respective bank or financial institution before applying.
            FinAssure operates as an analytical terminal and is not a financial intermediary.
          </p>
        </TiltCard>
      </div>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .font-sans { font-family: 'Plus Jakarta Sans', sans-serif !important; }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.05); border-radius: 20px; }
        .preserve-3d { transform-style: preserve-3d; }
        .shadow-shiny { box-shadow: 0 0 20px rgba(34, 197, 94, 0.2); }
        .shadow-6xl { box-shadow: 0 50px 100px -20px rgba(0,0,0,1); }
        
        /* Custom Neon Sliders */
        input[type='range'].loan-slider {
          -webkit-appearance: none;
          background: #1e293b;
          height: 6px;
          border-radius: 10px;
        }
        input[type='range'].loan-slider::-webkit-slider-runnable-track {
          width: 100%;
          height: 6px;
          cursor: pointer;
          border-radius: 10px;
          background: #1e293b;
        }
        /* Color the track before the thumb */
        input[type='range'].loan-slider::-webkit-slider-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #fff;
          cursor: pointer;
          -webkit-appearance: none;
          margin-top: -7px;
          box-shadow: 0 0 15px rgba(255,255,255,0.8), 0 0 5px rgba(34, 197, 94, 0.5);
          border: 3px solid #16A34A;
          transition: all 0.2s ease;
        }
        input[type='range'].loan-slider:hover::-webkit-slider-thumb {
          transform: scale(1.1);
          box-shadow: 0 0 20px rgba(255,255,255,1), 0 0 10px rgba(34, 197, 94, 0.8);
        }
        input[type='range'].loan-slider:active::-webkit-slider-runnable-track {
           background: #1e293b;
        }
      `}</style>
    </DashboardLayout>
  );
};

export default Loans;